import React, { forwardRef } from 'react';
import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ComponentPropsWithoutRef<'button'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Stretches the button to fill its container */
  fullWidth?: boolean;
  /** Icon or element placed before the label */
  startIcon?: React.ReactNode;
  /** Icon or element placed after the label */
  endIcon?: React.ReactNode;
  loading?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      startIcon,
      endIcon,
      loading = false,
      disabled,
      className,
      children,
      ...rest
    },
    ref,
  ) => {
    const classes = [
      styles.button,
      styles[variant],
      size !== 'md' && styles[size],
      fullWidth && styles.fullWidth,
      loading && styles.loading,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <button ref={ref} className={classes} disabled={disabled || loading} {...rest}>
        {loading && <span className={styles.spinner} aria-hidden />}
        {!loading && startIcon && <span className={styles.icon}>{startIcon}</span>}
        {children}
        {!loading && endIcon && <span className={styles.icon}>{endIcon}</span>}
      </button>
    );
  },
);

Button.displayName = 'Button';

export { Button };
